import { useEffect, useRef, useCallback } from 'react';

/**
 * useSectionHighlight — Reads `?section=sectionId` from URL,
 * expands the target accordion, scrolls to it, and applies a flash highlight.
 *
 * Usage in any split view:
 *   const { highlightRef } = useSectionHighlight(expandedSections, setExpandedSections);
 *   // On each accordion: ref={section.id === targetId ? highlightRef : null}
 *
 * @param {Object} expandedSections - current expanded state { [sectionId]: boolean }
 * @param {Function} setExpandedSections - setter for expanded state
 * @returns {{ targetSectionId: string|null, highlightRef: React.RefObject }}
 */
const useSectionHighlight = (expandedSections, setExpandedSections) => {
  const highlightRef = useRef(null);
  const processedRef = useRef(null); // Prevent double-processing

  // Read ?section= from URL
  const urlParams = new URLSearchParams(window.location.search);
  const targetSectionId = urlParams.get('section');

  useEffect(() => {
    if (!targetSectionId || processedRef.current === targetSectionId) return;

    // Mark as processed
    processedRef.current = targetSectionId;

    // Expand the target section's accordion
    setExpandedSections(prev => ({ ...prev, [targetSectionId]: true }));

    // Wait for accordion to expand, then scroll and highlight
    const timer = setTimeout(() => {
      if (highlightRef.current) {
        highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Flash highlight animation
        const el = highlightRef.current;
        el.style.transition = 'background-color 0.3s ease-in';
        el.style.backgroundColor = '#fff9c4'; // Yellow flash
        setTimeout(() => {
          el.style.transition = 'background-color 2s ease-out';
          el.style.backgroundColor = 'transparent';
        }, 1500);
      }
    }, 300); // Wait for accordion expand animation

    // Clean up URL param after highlight (don't leave it in URL)
    const cleanUrl = new URL(window.location);
    cleanUrl.searchParams.delete('section');
    window.history.replaceState({}, '', cleanUrl.toString());

    return () => clearTimeout(timer);
  }, [targetSectionId, setExpandedSections]);

  // Helper to check if a section should get the highlight ref
  const shouldHighlight = useCallback((sectionId) => {
    if (!targetSectionId) return false;
    const target = targetSectionId.toLowerCase();
    const sid = (sectionId || '').toLowerCase();
    // Exact match
    if (sid === target) return true;
    // Section ID contains the target (e.g., sid="f001-subscriber-api" matches target="f001")
    if (sid.startsWith(target + '-') || sid.startsWith(target)) return true;
    // Target contains the section ID
    if (target.startsWith(sid + '-') || target.startsWith(sid)) return true;
    // Strip category- prefix for matching
    const sidClean = sid.replace(/^category-/, '');
    const targetClean = target.replace(/^category-/, '');
    if (sidClean === targetClean) return true;
    if (sidClean.startsWith(targetClean + '-') || targetClean.startsWith(sidClean + '-')) return true;
    return false;
  }, [targetSectionId]);

  return { targetSectionId, highlightRef, shouldHighlight };
};

export default useSectionHighlight;
